// Modal "Viewer" de proposta gerada — usado pela ListaGeradas (aba propostas/vendas)
// e também via import externo (NegocioDetalhe, HydraDrawer).
// Inclui LinkPublicoBox (sub-componente só usado aqui).
// Extraído de ComercialPropostas.tsx durante refactor topológico.
//
// A assinatura digital saiu daqui em 21/08/2026: o antigo D4SignStatusBox lia
// colunas `d4sign_*` e se atualizava por polling de 30s. Agora são
// `CaixaAssinatura` (lê o envelope do nosso banco) e o modal compartilhado de
// escolha de signatários.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { App as AntApp } from 'antd';
import { API_URL } from '../../config';
import { DADOS_DEFAULT } from './types';
import { getToken, fmtDate } from './utils';
import {
  btnSec, btnPri, modalOverlay, modalContent,
} from './styles';
import { StatusBadge } from './StatusBadge';
import { EditorEstruturado } from './EditorEstruturado';
import CaixaAssinatura from './CaixaAssinatura';
import ModalAssinaturaDigital from '../../components/assinatura/ModalAssinaturaDigital';
import ModalHistoricoProposta from './ModalHistoricoProposta';
import { SignatarioEnvio } from '../../components/assinatura/tipos';
import { sugestaoSignatarios, enviarVendaParaAssinatura } from './apiAssinatura';
import ModalNovaComposicao from '../ModalNovaComposicao';
import { EditorComposicaoInline } from '../ComercialComposicoes';
import {
  mesclarVinculoComposicao,
  propostaTemAlteracoes,
} from './fluxoPropostaComposicao';
import { listarAreas } from './apiCatalogo';
import type { Subarea } from '../../utils/areasEmpresa';
import { mapaDeServicos, prepararParaSalvar, problemasDosItens, resumoDosProblemas } from './areaDoItem';

export function ViewerProposta({ id, onClose, onSaved }: { id: string; onClose: () => void; onSaved: () => void }) {
  // currentId permite trocar para outro doc internamente (ex.: nova revisão) sem fechar e reabrir.
  const [currentId, setCurrentId] = useState(id);
  useEffect(() => { setCurrentId(id); }, [id]);

  const [doc, setDoc] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [status, setStatus] = useState('rascunho');
  const [revisao, setRevisao] = useState(0);
  const [dados, setDados] = useState<any>(DADOS_DEFAULT);
  const [itensVenda, setItensVenda] = useState<any[]>([]);
  const [servicos, setServicos] = useState<any[]>([]);
  // Sub-áreas do catálogo (05/10/2026): a coluna Área do quadro de itens precisa
  // delas para saber quando a sub-área é obrigatória. `null` = ainda não chegou
  // (ou falhou): aí a tela confere só a área, e o servidor confere o resto.
  const [subareas, setSubareas] = useState<Subarea[] | null>(null);
  const [areasComErro, setAreasComErro] = useState(false);
  const [salvando, setSalvando] = useState(false);
  // A composição abre sobre a proposta. Assim o editor e todo o texto ainda
  // não salvo continuam montados em memória enquanto a pessoa trabalha nela.
  const [criandoComposicao, setCriandoComposicao] = useState(false);
  const [composicaoAbertaId, setComposicaoAbertaId] = useState<string | null>(null);
  const [composicaoPendenteVinculo, setComposicaoPendenteVinculo] = useState<string | null>(null);
  const [versaoComposicao, setVersaoComposicao] = useState(0);
  // Assinatura digital (só documento de venda)
  const [modalAssinatura, setModalAssinatura] = useState(false);
  const [sugestao, setSugestao] = useState<SignatarioEnvio[]>([]);
  // Quando o sistema não conseguiu sugerir uma das duas partes, o modal diz o
  // que falta e onde resolver — em vez de abrir com uma pessoa só, em silêncio.
  const [avisoSugestao, setAvisoSugestao] = useState<string | null>(null);
  // O Anexo I (proposta técnica) que vai DENTRO do documento assinado —
  // 17/09/2026. A ordem de serviço descreve o "Objeto" em uma linha; sem isso
  // o cliente assinava a formalização sem o escopo (VND-2026-079).
  const [anexoAssinatura, setAnexoAssinatura] = useState<
    { tem: boolean; proposta: string | null; secoes: string[] } | null>(null);
  const [carregandoSugestao, setCarregandoSugestao] = useState(false);
  // Força a CaixaAssinatura a recarregar depois de um envio — sem isto ela
  // continuaria mostrando o estado anterior (ou nada) até alguém reabrir o
  // documento, e quem acabou de enviar concluiria que o envio não funcionou.
  const [versaoAssinatura, setVersaoAssinatura] = useState(0);
  const [avisoAtualizado, setAvisoAtualizado] = useState(false); // mostra "Dwight atualizou — clique pra ver"
  // Histórico de versões (15/09/2026): toda gravação guarda o estado anterior por 12 meses.
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const rotuloDocumento = doc?.tipo === 'venda' ? 'venda' : 'proposta';

  const propostaAlterada = useMemo(() => propostaTemAlteracoes(doc, {
    titulo, status, revisao, dados, itensVenda,
  }, DADOS_DEFAULT), [doc, titulo, status, revisao, dados, itensVenda]);

  // Protege também contra atualizar/fechar a aba do navegador enquanto há
  // trabalho não salvo. A mensagem é controlada pelo próprio navegador.
  useEffect(() => {
    if (!editando || !propostaAlterada) return;
    function protegerSaida(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', protegerSaida);
    return () => window.removeEventListener('beforeunload', protegerSaida);
  }, [editando, propostaAlterada]);

  function confirmarDescarteProposta(): boolean {
    return !editando || !propostaAlterada || window.confirm(
      `Descartar as alterações ainda não salvas desta ${rotuloDocumento}?\n\n` +
      'Se você continuar, o texto e os ajustes feitos desde o último salvamento serão perdidos.'
    );
  }

  function fecharViewer() {
    if (confirmarDescarteProposta()) onClose();
  }

  function cancelarEdicao() {
    if (!confirmarDescarteProposta()) return;
    setEditando(false);
    setTitulo(doc.titulo);
    setStatus(doc.status);
    setRevisao(doc.revisao || 0);
    setDados(doc.dados_estruturados || { ...DADOS_DEFAULT });
    setItensVenda(Array.isArray(doc.itens_venda) ? doc.itens_venda : []);
  }

  function abrirCriacaoComposicao() {
    if (!doc?.proposta_id) {
      alert(`Esta ${rotuloDocumento} não está vinculada a um negócio. Salve o vínculo antes de criar uma composição.`);
      return;
    }
    setCriandoComposicao(true);
  }

  function abrirEdicaoComposicao(composicaoId: string) {
    setComposicaoPendenteVinculo(null);
    setComposicaoAbertaId(composicaoId);
  }

  function fecharEdicaoComposicao() {
    setComposicaoAbertaId(null);
    setComposicaoPendenteVinculo(null);
  }

  async function vincularComposicaoNova(composicaoId: string) {
    try {
      const token = await getToken();
      const res = await axios.put(`${API_URL}/comercial/propostas-geradas/${currentId}`,
        { composicao_id: composicaoId },
        { headers: { Authorization: token } });
      const atualizado = res.data.body ? JSON.parse(res.data.body) : res.data;
      setDoc((anterior: any) => mesclarVinculoComposicao(anterior, composicaoId, atualizado));
      onSaved();
    } catch (e: any) {
      throw new Error(
        'A composição foi salva, mas não foi possível vinculá-la à proposta. ' +
        (e.response?.data?.error || e.message)
      );
    }
  }

  // Carrega doc — refatorado pra ser chamado tanto no mount quanto em refresh
  async function carregarDoc(silencioso = false) {
    if (!silencioso) setLoading(true);
    try {
      const token = await getToken();
      const [docRes, srvRes] = await Promise.all([
        axios.get(`${API_URL}/comercial/propostas-geradas/${currentId}`, { headers: { Authorization: token } }),
        axios.get(`${API_URL}/comercial/servicos-produtos`, { headers: { Authorization: token } }),
      ]);
      const data = docRes.data.body ? JSON.parse(docRes.data.body) : docRes.data;
      const srv = srvRes.data.body ? JSON.parse(srvRes.data.body) : srvRes.data;
      setDoc(data);
      setTitulo(data.titulo || '');
      setStatus(data.status || 'rascunho');
      setRevisao(data.revisao || 0);
      setDados(data.dados_estruturados || { ...DADOS_DEFAULT });
      setItensVenda(Array.isArray(data.itens_venda) ? data.itens_venda : []);
      setServicos(Array.isArray(srv) ? srv : []);
      if (!silencioso) setEditando(false);
      setAvisoAtualizado(false);
    } finally { if (!silencioso) setLoading(false); }
  }
  useEffect(() => { carregarDoc(); /* eslint-disable-next-line */ }, [currentId]);

  async function carregarAreas() {
    setAreasComErro(false);
    try {
      const areas = await listarAreas();
      setSubareas(areas.flatMap(a => a.subareas));
    } catch { setAreasComErro(true); }
  }
  useEffect(() => { carregarAreas(); }, []);

  // O que falta de área no quadro de itens — trava o "Salvar" (areaDoItem.ts).
  const mapaServicos = useMemo(() => mapaDeServicos(servicos), [servicos]);
  const problemasArea = useMemo(
    () => (editando ? problemasDosItens(itensVenda, mapaServicos, subareas) : []),
    [editando, itensVenda, mapaServicos, subareas]);
  const avisoArea = resumoDosProblemas(problemasArea);

  // ── Auto-refresh: detecta quando o Dwight (web ou Telegram) atualizou esta proposta ──
  // Polling silencioso a cada 8s — só dispara recarga se atualizado_em mudou.
  useEffect(() => {
    if (!doc?.atualizado_em || !currentId) return;
    const intervalo = setInterval(async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/comercial/propostas-geradas/${currentId}`, {
          headers: { Authorization: token },
        });
        const fresh = r.data.body ? JSON.parse(r.data.body) : r.data;
        if (fresh?.atualizado_em && fresh.atualizado_em !== doc.atualizado_em) {
          if (editando) {
            // Usuário tá editando — não sobrescreve, só avisa
            setAvisoAtualizado(true);
          } else {
            await carregarDoc(true);
          }
        }
      } catch { /* ignora erros transientes do polling */ }
    }, 8000);
    return () => clearInterval(intervalo);
    // eslint-disable-next-line
  }, [doc?.atualizado_em, currentId, editando]);

  // ── Event listener: drawer Dwight dispara após aplicar ação que afeta proposta ──
  useEffect(() => {
    function onAcaoAplicada(e: any) {
      const detail = e.detail || {};
      const tool = detail.tool;
      const resultado = detail.resultado;
      // Verifica se essa ação afetou A NOSSA proposta
      const afetaEssa = (
        (tool === 'gerar_proposta' && resultado?.criada?.id === currentId) ||
        (tool === 'atualizar_proposta_documento' && resultado?.atualizada?.numero_proposta === doc?.numero_proposta)
      );
      if (afetaEssa) {
        if (editando) setAvisoAtualizado(true);
        else carregarDoc(true);
      }
    }
    window.addEventListener('hydra-acao-aplicada', onAcaoAplicada);
    return () => window.removeEventListener('hydra-acao-aplicada', onAcaoAplicada);
    // eslint-disable-next-line
  }, [currentId, doc?.numero_proposta, editando]);

  // Devolve true só se GRAVOU — quem chama (gerar o PDF) não pode seguir com o
  // documento antigo achando que salvou.
  async function salvar(): Promise<boolean> {
    // Toda linha com valor precisa de área (e de sub-área, quando a área tem):
    // é dos itens que sai a divisão por área do contrato. O motivo está escrito
    // ao lado do botão; aqui é a trava.
    if (problemasArea.length) return false;
    setSalvando(true);
    try {
      const token = await getToken();
      const res = await axios.put(`${API_URL}/comercial/propostas-geradas/${currentId}`,
        { titulo, status, revisao,
          dados_estruturados: dados,
          // Cada linha vai com a área, a sub-área e o código que estão valendo
          // para ela; `validar_areas` pede ao servidor a mesma conferência.
          itens_venda: prepararParaSalvar(itensVenda, mapaServicos),
          validar_areas: true,
        },
        { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setDoc(data);
      // A tela fica com o que o servidor guardou (as linhas foram com a área
      // escrita nelas): sem isto, "Editar → Cancelar" logo depois de salvar
      // perguntaria se quer descartar alterações que ninguém fez.
      setItensVenda(Array.isArray(data.itens_venda) ? data.itens_venda : []);
      if (data.dados_estruturados) setDados(data.dados_estruturados);
      setEditando(false);
      onSaved();
      return true;
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
      return false;
    } finally { setSalvando(false); }
  }

  // 🔄 Re-puxa cliente / contato / empresa / vendedor pra dentro da proposta
  // JÁ gerada, SEM tocar nos textos, itens e valores que o usuário editou.
  // Usa regenerar_html=true: o backend re-monta o contexto fresco (cliente e
  // contato de comercial.propostas — que o "Trocar cliente" da oportunidade
  // atualiza; empresa de admin.empresa_dados) e re-renderiza o HTML mantendo o
  // quadro editado. Caso de uso do Lucas: trocou o cliente/empresa na
  // oportunidade e quer refletir numa proposta feita antes da troca.
  async function atualizarDados() {
    if (!doc) return;
    if (!window.confirm(
      'Atualizar os dados de cliente, contato e empresa nesta proposta?\n\n' +
      'Puxa os dados atuais da oportunidade — útil se você trocou o cliente ou a ' +
      'empresa depois de gerar a proposta. Os textos, itens e valores que você ' +
      'editou são preservados.'
    )) return;
    setSalvando(true);
    try {
      const token = await getToken();
      const res = await axios.put(`${API_URL}/comercial/propostas-geradas/${currentId}`,
        { regenerar_html: true },
        { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setDoc(data);
      onSaved();
      await carregarDoc(false);
    } catch (e: any) {
      alert('Erro ao atualizar dados: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function baixarPdf() {
    if (!doc) return;
    // Padrão async + polling: API Gateway tem timeout de 29s, mas Chromium
    // (cold start) + render pode passar disso. O backend dispara um job
    // assíncrono e o front consulta `?mode=check` a cada 3s até ficar pronto.
    const btn: any = document.activeElement;
    function setBtn(txt: string, disabled = true) {
      if (btn && btn.tagName === 'BUTTON') {
        if (!btn.dataset.txt) btn.dataset.txt = btn.innerText;
        btn.disabled = disabled;
        btn.innerText = txt;
      }
    }
    function restoreBtn() {
      if (btn && btn.tagName === 'BUTTON' && btn.dataset.txt) {
        btn.disabled = false;
        btn.innerText = btn.dataset.txt;
        delete btn.dataset.txt;
      }
    }
    try {
      if (editando) {
        if (!window.confirm('Você tem alterações não salvas. Deseja salvar antes de gerar o PDF?')) return;
        if (avisoArea) { alert(avisoArea); return; }
        if (!(await salvar())) return;
      }
      const token = await getToken();
      setBtn('Iniciando PDF...');
      // 1) START: dispara geração async
      const startRes = await axios.post(`${API_URL}/pdf/proposta/${currentId}?mode=start`, {},
        { headers: { Authorization: token }, timeout: 25000 });
      const startData = startRes.data.body ? JSON.parse(startRes.data.body) : startRes.data;
      const startedAt = startData.started_at || Date.now();

      // 2) POLL: verifica a cada 3s até 90s (cobre cold start do Chromium)
      const maxTentativas = 30;
      for (let i = 1; i <= maxTentativas; i++) {
        await new Promise(r => setTimeout(r, 3000));
        setBtn(`Gerando PDF (${i * 3}s)...`);
        try {
          const checkRes = await axios.post(
            `${API_URL}/pdf/proposta/${currentId}?mode=check&started_at=${startedAt}`,
            {},
            { headers: { Authorization: token }, timeout: 15000 }
          );
          const checkData = checkRes.data.body ? JSON.parse(checkRes.data.body) : checkRes.data;
          if (checkData.status === 'ready' && checkData.url) {
            restoreBtn();
            window.open(checkData.url, '_blank');
            return;
          }
        } catch { /* tenta novamente */ }
      }
      restoreBtn();
      alert('A geração do PDF demorou mais que 90s. Tente novamente em alguns instantes — o Chromium pode estar em cold start.');
    } catch (e: any) {
      restoreBtn();
      alert('Erro ao gerar PDF: ' + (e.response?.data?.error || e.message));
    }
  }

  async function publicar() {
    try {
      const token = await getToken();
      const res = await axios.post(`${API_URL}/comercial/propostas-geradas/${currentId}/publicar`, {},
        { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      // Recarrega o doc completo (status pode ter mudado para "enviada" no backend)
      // e notifica o parent (NegocioDetalhe) pra recarregar a lista de propostas.
      setDoc({ ...doc, link_hash: data.link_hash, status: data.status || doc.status });
      onSaved();
      await carregarDoc(false);
    } catch (e: any) { alert('Erro: ' + e.message); }
  }

  async function novaRevisao() {
    if (!window.confirm(
      `Criar nova revisão deste documento?\n\n` +
      `A versão atual (R${doc.revisao || 0}) será mantida no histórico e uma nova revisão será criada como rascunho.`
    )) return;
    try {
      const token = await getToken();
      const res = await axios.post(`${API_URL}/comercial/propostas-geradas/${currentId}/revisar`, {},
        { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      if (!data?.id) throw new Error('Resposta inválida do servidor');
      onSaved();           // refresca lista do parent (ex.: NegocioDetalhe)
      setCurrentId(data.id); // troca o viewer para a nova revisão (recarrega via useEffect)
    } catch (e: any) {
      alert('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  // Assinatura digital (2026-08-21): abre o modal com a sugestão de quem
  // assina já preenchida — contato do cliente e vendedor, que era o que o
  // caminho antigo IMPUNHA. Aqui vira ponto de partida, e a pessoa edita.
  //
  // A sugestão vem do backend porque ela sai de `variaveis_snap`, o retrato do
  // documento no momento em que foi gerado — o mesmo que o PDF mostra.
  // Recalcular no front duplicaria a regra e as duas versões divergiriam.
  async function abrirEnvioAssinatura() {
    if (!doc) return;
    setCarregandoSugestao(true);
    try {
      const r = await sugestaoSignatarios(currentId);
      setSugestao(r.signatarios || []);
      setAvisoSugestao(r.aviso || null);
      setAnexoAssinatura(r.anexo || null);
      setModalAssinatura(true);
    } catch (e: any) {
      alert('Não consegui montar a lista de signatários: '
            + (e.response?.data?.error || e.message));
    } finally { setCarregandoSugestao(false); }
  }

  async function despublicar() {
    if (!window.confirm('Revogar o link público? Quem tiver o link não conseguirá mais acessar.')) return;
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas/${currentId}/despublicar`, {},
        { headers: { Authorization: token } });
      setDoc({ ...doc, link_hash: null });
    } catch (e: any) { alert('Erro: ' + e.message); }
  }

  function imprimir() {
    // Abre janela limpa só com o conteúdo + CSS de impressão; usa window.print() (gera PDF nativo)
    if (!doc) return;
    const w = window.open('', '_blank', 'width=900,height=900');
    if (!w) { alert('Bloqueador de pop-up impediu a abertura. Permita pop-ups e tente novamente.'); return; }
    // Reescreve URLs antigas (Azure Blob/Ploomes) para nosso S3 — para propostas
    // geradas antes da correção. Mantém a paridade com pdf-generator.
    const conteudo = (doc.conteudo_final || '')
      .replace(/https:\/\/stgploomescrmprd01\.blob\.core\.windows\.net\/crm-prd\/D6E110119333\/Images\/d24424148a0f4fe4b1e7daf00cca56bd\.png/g,
        'https://dre-uploads-471112958734.s3.us-east-1.amazonaws.com/assets/proposta/capa-default-bg.png')
      .replace(/https:\/\/stgploomescrmprd01\.blob\.core\.windows\.net\/crm-prd\/D6E110119333\/Images\/f72d4ddf2ec940ceb5fe6589ffc9accb\.png/g,
        'https://dre-uploads-471112958734.s3.us-east-1.amazonaws.com/assets/proposta/capa-default-logo.png');

    // [86cd8b7a-fix6] @page rules duplicadas no <head> (incluindo named page
    // 'interna' com @bottom margin boxes) garantem que o navegador aplique
    // mesmo em ambientes onde @page dentro de <style> em body é ignorado.
    // Isso era o motivo da capa quebrar: @page interna do body era ignorado,
    // .pm-pagina caía pra @page default sem margem, e somado a issues de
    // flex em print-mode, a capa transbordava.
    w.document.write(`<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"><title>${doc.titulo}</title>
<style>
  /* @page rules SEMPRE no head — funcionam de forma consistente em todos browsers */
  @page { size: A4 portrait; margin: 0; }
  @page interna {
    size: A4 portrait;
    margin: 22mm 18mm 18mm 18mm;
    @bottom-left {
      content: 'Hydrodata Engenharia';
      font-family: 'Inter', sans-serif;
      font-size: 8.5px; color: #aaa;
    }
    @bottom-right {
      content: counter(page);
      font-family: 'Inter', sans-serif;
      font-size: 8.5px; color: #aaa;
    }
  }
  html, body { margin: 0; padding: 0; }
  /* Compat com propostas SEM o CSS embutido novo (legado): aplica margem confortável. */
  body:not(:has(.proposta-render)) {
    font-family: 'Helvetica', Arial, sans-serif; max-width: 760px;
    margin: 30px auto; padding: 0 20px; color: #222; line-height: 1.5;
  }
  /* Override do logo (cobre propostas antigas com max-width: none em print) */
  .proposta-render .capa-logo-img img,
  .capa-logo-img img,
  img[alt="Hydrodata"] { max-width: 180px !important; height: auto !important; }
  /* [86cd8b7a-fix7] Capa moderna em print: 100vw/100vh + layout absolute.
     Mesma regra do conteudo_final, duplicada aqui pra garantir aplicação no
     contexto da janela popup. vh/vw adaptam ao tamanho real que o navegador
     usa pra A4 (evita 1-2mm de overflow que empurrava rodape pra pag 2). */
  @media print {
    .pm-capa {
      width: 100vw !important;
      height: 100vh !important;
      min-height: 100vh !important;
      max-height: 100vh !important;
      aspect-ratio: unset !important;
      overflow: hidden !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      page-break-after: always !important;
      break-after: page !important;
      display: block !important;
      position: relative !important;
    }
    .pm-capa-topo {
      position: absolute !important;
      top: 28mm !important; left: 22mm !important; right: 22mm !important;
    }
    .pm-capa-meio {
      position: absolute !important;
      top: 50% !important; left: 22mm !important; right: 22mm !important;
      transform: translateY(-50%) !important;
      margin: 0 !important;
    }
    .pm-capa-rodape {
      position: absolute !important;
      bottom: 28mm !important; right: 22mm !important; left: 22mm !important;
    }
    .pm-pagina { padding: 0 !important; }
    .pm-page-footer { display: none !important; }
    .proposta-render .capa-logo-img img,
    .capa-logo-img img,
    img[alt="Hydrodata"] { max-width: 180px !important; height: auto !important; }
  }
</style></head><body>${conteudo}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 600);
  }

  if (loading) return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalContent}><div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Carregando...</div></div>
    </div>
  );
  if (!doc) return null;

  // Visualização e edição usam toda a janela. Antes, a prévia ficava limitada a
  // 920 px e 65vh, desperdiçando a maior parte de monitores grandes.
  const modalDinamico: React.CSSProperties = {
    background: '#fff', borderRadius: 0, padding: 18,
    width: '100%', height: '100vh', maxWidth: 'none', maxHeight: '100vh',
    overflow: 'hidden', display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
  };
  const overlayDinamico: React.CSSProperties = {
    ...modalOverlay, background: '#FAFBFF', alignItems: 'flex-start', justifyContent: 'flex-start',
  };

  return (
    // <AntApp> aqui e não só no ComercialPropostas: este viewer também é
    // aberto pelo NegocioDetalhe e pelo HydraDrawer, e sem o contexto do App
    // os avisos (`message`) da caixa de assinatura e do modal saem em silêncio,
    // com aviso só no console. Quem clicasse em "Verificar agora" veria
    // literalmente nada acontecer.
    <AntApp>
    <div style={overlayDinamico} onClick={editando ? undefined : fecharViewer}>
      <div style={modalDinamico} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap', flexShrink: 0 }}>
          {editando ? (
            <input value={titulo} onChange={e => setTitulo(e.target.value)}
              style={{ flex: 1, fontSize: 15, fontWeight: 600, color: '#020D7F', border: '0.5px solid #ddd', padding: '5px 8px', borderRadius: 4 }} />
          ) : (
            <h3 style={{ flex: 1, minWidth: 220, margin: 0, fontSize: 15, color: '#020D7F' }}>{doc.titulo}</h3>
          )}
          <span style={{ fontSize: 10, color: '#888', whiteSpace: 'nowrap' }}>
            {doc.numero_proposta || `v${doc.versao}`}{doc.revisao > 0 ? ` · Rev.${doc.revisao}` : ''}
            {' · '}{fmtDate(doc.atualizado_em)}
          </span>
          {editando ? (
            <select value={status} onChange={e => setStatus(e.target.value)}
              style={{ fontSize: 11, padding: '4px 8px', borderRadius: 4, border: '0.5px solid #ddd' }}>
              <option value="rascunho">Rascunho</option>
              <option value="enviada">Enviada</option>
              <option value="aprovada">Aprovada</option>
            </select>
          ) : <StatusBadge status={doc.status} />}
          <button onClick={fecharViewer} style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>×</button>
        </div>

        {avisoAtualizado && (
          <div style={{
            padding: 8, marginBottom: 8, borderRadius: 5,
            background: '#FFF8E8', border: '0.5px solid #B07500',
            display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: '#B07500',
          }}>
            ◇ <strong>O Dwight atualizou esta proposta.</strong>
            <button onClick={() => carregarDoc(false)}
              style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 11, fontWeight: 600,
                border: 'none', borderRadius: 4, background: '#B07500', color: '#fff', cursor: 'pointer' }}>
              {editando ? 'Recarregar (perde edição)' : 'Recarregar'}
            </button>
            <button onClick={() => setAvisoAtualizado(false)}
              style={{ padding: '4px 8px', fontSize: 11, border: 'none', background: 'transparent', color: '#B07500', cursor: 'pointer' }}>
              Ignorar
            </button>
          </div>
        )}

        {editando ? (
          <div style={{ flex: 1, overflow: 'auto', padding: 4 }}>
            <EditorEstruturado
              key={`${doc?.composicao_id || 'sem-composicao'}:${versaoComposicao}`}
              dados={dados} setDados={setDados}
              itensVenda={itensVenda} setItensVenda={setItensVenda}
              servicos={servicos}
              subareas={subareas}
              areasComErro={areasComErro}
              onRecarregarAreas={carregarAreas}
              onServicoCriado={(novo) => setServicos(lista => (lista.some(s => s.id === novo.id) ? lista : [...lista, novo]))}
              onSubareaCriada={(nova) => setSubareas(lista => (lista && !lista.some(s => s.id === nova.id) ? [...lista, nova] : lista))}
              revisao={revisao}
              composicaoId={doc?.composicao_id || null}
              propostaGeradaId={currentId}
              negocioId={doc?.proposta_id || null}
              tipo={doc?.tipo === 'venda' ? 'venda' : 'proposta'}
              onCriarComposicao={abrirCriacaoComposicao}
              onEditarComposicao={abrirEdicaoComposicao}
              onComposicaoVinculada={(composicaoId, atualizado) => {
                setDoc((anterior: any) => mesclarVinculoComposicao(
                  anterior, composicaoId, atualizado,
                ));
                setVersaoComposicao(v => v + 1);
                onSaved();
              }}
            />
          </div>
        ) : (
          <div style={{
            border: '0.5px solid #e0e0e0', borderRadius: 5, padding: 24,
            flex: 1, minHeight: 0, overflow: 'auto', background: '#fff',
            fontSize: 13, lineHeight: 1.5, color: '#222',
          }} dangerouslySetInnerHTML={{ __html: doc.conteudo_final }} />
        )}

        {/* Aceite registrado — exibido se houver */}
        {!editando && doc.aceite_em && (
          <div style={{
            marginTop: 12, padding: 10, background: '#0a7f4a15',
            border: '0.5px solid #0a7f4a', borderRadius: 5, fontSize: 12, color: '#0a7f4a',
          }}>
            ✓ Aceito por <strong>{doc.aceite_nome}</strong> em {new Date(doc.aceite_em).toLocaleString('pt-BR')}
            {doc.aceite_ip && <span style={{ color: '#666', fontSize: 10 }}> · IP: {doc.aceite_ip}</span>}
          </div>
        )}

        {/* Link público — exibido se publicado */}
        {!editando && doc.link_hash && (
          <LinkPublicoBox hash={doc.link_hash} onDespublicar={async () => {
            await despublicar();
          }} />
        )}

        {/* Assinatura digital — só em documento de VENDA. Proposta usa o aceite
            digital simples pelo link público (mandar proposta para a D4 Sign
            gastaria crédito num documento que ainda vai mudar).
            A caixa se esconde sozinha quando nunca houve envio. */}
        {!editando && doc.tipo === 'venda' && (
          <CaixaAssinatura key={versaoAssinatura} docId={currentId}
            recarregar={() => { onSaved(); carregarDoc(false); }} />
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14, flexWrap: 'wrap', flexShrink: 0 }}>
          {editando ? (
            <>
              {/* O botão fica apagado enquanto falta área em algum item — o motivo
                  tem que estar escrito (o `title` do botão não existe no celular). */}
              {avisoArea && (
                <span role="alert" style={{ flex: '1 1 260px', alignSelf: 'center', fontSize: 12, color: '#B07500', textAlign: 'right' }}>
                  {avisoArea}
                </span>
              )}
              <button onClick={cancelarEdicao} style={btnSec}>Cancelar</button>
              <button onClick={salvar} disabled={salvando || !!avisoArea} title={avisoArea || undefined}
                style={{ ...btnPri, ...(avisoArea ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>
                {salvando ? 'Salvando...' : 'Salvar alterações'}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setEditando(true)} style={btnSec}>✏️ Editar</button>
              <button onClick={atualizarDados} disabled={salvando}
                style={{ ...btnSec, color: '#0E7490', borderColor: '#0E7490' }}
                title="Re-puxa cliente, contato e empresa da oportunidade (preserva os textos, itens e valores que você editou). Use se trocou o cliente/empresa depois de gerar a proposta.">
                🔄 {salvando ? 'Atualizando...' : 'Atualizar dados'}
              </button>
              <button onClick={novaRevisao}
                style={{ ...btnSec, color: '#7C3AED', borderColor: '#7C3AED' }}
                title="Cria uma nova revisão preservando esta no histórico">
                ↻ Nova revisão
              </button>
              <button onClick={() => setHistoricoAberto(true)} style={btnSec}
                title="Como esta proposta estava antes de cada gravação (tela, Dwight ou sistema), com o que mudou e botão de restaurar">
                🕘 Histórico
              </button>
              {!doc.link_hash && (
                <button onClick={publicar} style={{ ...btnSec, color: '#B07500', borderColor: '#B07500' }}>
                  🌐 Publicar link
                </button>
              )}
              <button onClick={imprimir} style={btnSec}>🖨️ Imprimir local</button>
              {/* Assinatura: somente para documentos de VENDA — proposta usa
                  aceite digital simples pelo link público. */}
              {doc.tipo === 'venda' && (
                <button onClick={abrirEnvioAssinatura} disabled={carregandoSugestao}
                  style={{ ...btnSec, color: '#16A34A', borderColor: '#16A34A' }}
                  title="Escolha quem assina e envie o PDF para assinatura digital">
                  ✍️ {carregandoSugestao ? 'Abrindo…' : 'Enviar para assinatura'}
                </button>
              )}
              <button onClick={baixarPdf} style={btnPri}>📥 Baixar PDF</button>
            </>
          )}
        </div>
      </div>

    </div>

    {criandoComposicao && doc?.proposta_id && (
      <ModalNovaComposicao
        propostaId={Number(doc.proposta_id)}
        tituloNegocio={doc.negocio_titulo || doc.titulo || 'Negócio'}
        onClose={() => setCriandoComposicao(false)}
        onCriada={(composicaoId) => {
          setCriandoComposicao(false);
          setComposicaoPendenteVinculo(composicaoId);
          setComposicaoAbertaId(composicaoId);
        }}
      />
    )}

    {composicaoAbertaId && (
      <EditorComposicaoInline
        composicaoId={composicaoAbertaId}
        origemLabel={doc.numero_proposta ? `${rotuloDocumento} ${doc.numero_proposta}` : rotuloDocumento}
        onClose={fecharEdicaoComposicao}
        onSaved={async () => {
          if (composicaoPendenteVinculo === composicaoAbertaId) {
            await vincularComposicaoNova(composicaoAbertaId);
          }
          setVersaoComposicao(v => v + 1);
          fecharEdicaoComposicao();
        }}
      />
    )}

    {/* ⚠️ FORA do <div> do overlay, de propósito (fix 2026-08-24).
        O overlay fecha o viewer no onClick, e o Modal do antd renderiza em
        portal para o body — mas evento de portal no React sobe pela ÁRVORE DE
        COMPONENTES, não pela do DOM. Com o Modal declarado dentro do overlay,
        clicar em "Adicionar" disparava o onClick do overlay: o viewer fechava
        e o modal piscava e sumia junto. Declarado aqui como irmão, não há
        ancestral com handler de fechar. */}
    <ModalHistoricoProposta
      open={historicoAberto}
      propostaGeradaId={currentId}
      rotulo={doc.numero_proposta ? `${rotuloDocumento} ${doc.numero_proposta}${doc.revisao > 0 ? ` R${doc.revisao}` : ''}` : rotuloDocumento}
      onClose={() => setHistoricoAberto(false)}
      onRestaurada={() => { setEditando(false); onSaved(); carregarDoc(true); }}
    />
    <ModalAssinaturaDigital
      open={modalAssinatura}
      titulo={doc.numero_proposta || doc.titulo || 'documento'}
      preSelecionados={sugestao}
      // O documento de venda tem as duas linhas de assinatura (Contratada e
      // Contratante) e o servidor recusa o envio sem as duas.
      papeisObrigatorios={['Contratante', 'Contratada']}
      descricaoDoAviso={`Cada pessoa recebe um link da D4 Sign para assinar. O PDF é regerado com a versão mais recente do documento antes de sair — quem assina vê o que está na tela agora.`
        + (anexoAssinatura?.tem
          ? ` 📎 Vai junto o Anexo I — Proposta Técnica${anexoAssinatura.proposta ? ` (${anexoAssinatura.proposta})` : ''}`
            + `${anexoAssinatura.secoes?.length ? `, com ${anexoAssinatura.secoes.join(', ').toLowerCase()}` : ''}`
            + `: o documento assinado inclui o escopo, e não só a ordem de serviço. Confira em "Baixar PDF" antes de enviar.`
          : ' ⚠️ Esta venda não tem escopo técnico para o Anexo I — a ordem de serviço sozinha descreve o objeto em uma linha, e o envio vai ser recusado. Clique em "Editar" e preencha o bloco "Escopo técnico — vai no Anexo I".')
        + (avisoSugestao ? ` ⚠️ ${avisoSugestao}` : '')}
      onEnviar={(p) => enviarVendaParaAssinatura(currentId, p)}
      onClose={() => setModalAssinatura(false)}
      onEnviado={() => {
        setVersaoAssinatura(v => v + 1);
        onSaved();
        carregarDoc(false);
      }}
    />
    </AntApp>
  );
}

function LinkPublicoBox({ hash, onDespublicar }: { hash: string; onDespublicar: () => void }) {
  // URL "limpa" usando subdomínio dedicado SEMPRE em prod.
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host.startsWith('127.') || host.startsWith('192.168.');
  const url = isLocal
    ? `${window.location.origin}/publico/proposta/${hash}`
    : `https://propostas.hydrodataeng.com.br/${hash}`;
  // [d1c3a8f1] URL alternativa via api.hydrodataeng.com.br com ?share=1.
  // Quando colado no WhatsApp/LinkedIn/Slack, esse path retorna HTML com
  // og:tags dinâmicas (título da proposta, cliente, número) e meta refresh
  // que redireciona o navegador pra propostas.hydrodataeng.com.br/{hash}.
  // Bot vê preview rico, humano vê SPA real após click.
  const urlShare = isLocal
    ? `${window.location.origin}/publico/proposta/${hash}?share=1`
    : `https://api.hydrodataeng.com.br/publico/proposta/${hash}?share=1`;
  const [copiado, setCopiado] = useState<'normal' | 'share' | null>(null);
  function copiar(qual: 'normal' | 'share') {
    navigator.clipboard.writeText(qual === 'share' ? urlShare : url);
    setCopiado(qual);
    setTimeout(() => setCopiado(null), 2500);
  }
  return (
    <div style={{
      marginTop: 12, padding: 10, background: '#FFF8E8',
      border: '0.5px solid #B07500', borderRadius: 5,
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#B07500', marginBottom: 6 }}>
        🌐 Link público de visualização
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input value={url} readOnly onClick={e => e.currentTarget.select()}
          style={{
            flex: 1, fontSize: 11, padding: '5px 8px', borderRadius: 3,
            border: '0.5px solid #ddd', background: '#fff', fontFamily: 'monospace',
          }} />
        <button onClick={() => copiar('normal')} style={{
          padding: '5px 12px', fontSize: 11, borderRadius: 4,
          border: '0.5px solid #B07500', background: '#fff', color: '#B07500',
          cursor: 'pointer', fontWeight: 600,
        }}>{copiado === 'normal' ? '✓ Copiado!' : '📋 Copiar'}</button>
        <button onClick={() => copiar('share')} title="Link com preview rico no WhatsApp/LinkedIn (mostra título + cliente)"
          style={{
            padding: '5px 12px', fontSize: 11, borderRadius: 4,
            border: '0.5px solid #25D366', background: '#fff', color: '#25D366',
            cursor: 'pointer', fontWeight: 600,
          }}>{copiado === 'share' ? '✓ Copiado!' : '📲 WhatsApp'}</button>
        <button onClick={onDespublicar} style={{
          padding: '5px 12px', fontSize: 11, borderRadius: 4,
          border: '0.5px solid #B91626', background: '#fff', color: '#B91626',
          cursor: 'pointer',
        }} title="Revogar link público">Revogar</button>
      </div>
      <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
        Use <strong>📋 Copiar</strong> pra compartilhar normalmente. Use <strong>📲 WhatsApp</strong> pra mandar pelo WhatsApp/LinkedIn/Slack — o link traz preview com título da proposta e cliente.
      </div>
    </div>
  );
}
